<script setup lang="ts">
import { withoutTrailingSlash } from 'ufo'
import { mapContentNavigation } from '@nuxt/ui/utils/content'
import { findPageBreadcrumb } from '@nuxt/content/utils'
import type {ContentNavigationItem} from "@nuxt/content";

const route = useRoute()

const { data: post } = await useAsyncData(route.path, () => queryCollection('blog').path(useRoute().path).where('date', '<=', !useCookie('previewToken').value ? new Date().toISOString() : '9999-01-01').first(), {transform: p => {
    p.authors = (p.authors ?? []).map((a) => {
      return {...a, avatar: {src: `/team/${a.slug}.jpg`}}
    })
    p.badge = p.category ? {label: p.category} : null
    return p
  }})
if (!post.value) {
  throw createError({ statusCode: 404, fatal: true })
}

const { data: surround } = await useAsyncData(`${route.path}-surround`, () => queryCollectionItemSurroundings('blog', route.path, {
      fields: ['title', 'description'],
    }).where('date', '<=', !useCookie('previewToken').value ? new Date().toISOString() : '9999-01-01').order('date', 'DESC'))

useSeoMeta({
  title: post.value.title.replace('~~', '') ?? null,
  description: post.value.description ?? null,
  ogImage: `https://asso.${useDomain()}${useRoute().path.endsWith('/') ? useRoute().path.slice(0, -1) : useRoute().path}/thumbnail.jpg`,
  canonical: post.value.meta.canonical ?? null,
})

</script>

<template>
  <UContainer v-if="post">
    <UPageHeader
        :title="post.title"
        :description="post.description"
    >
      <template #headline>
        <UBreadcrumb :items="[{label: 'Blog', to: '/ressources'}]" />
      </template>
      <template #title>
        <MDC :value="post.title" unwrap="p" />
      </template>
      <div class="mt-4 text-sm font-semibold text-[var(--ui-primary)] flex items-center gap-1.5">
        <UBadge v-if="post.badge"
                v-bind="post.badge"
                variant="subtle"
        />
        <span class="text-gray-500 dark:text-gray-400" v-if="post.badge">&middot;</span>
        <time class="text-gray-500 dark:text-gray-400">{{ new Date(post.date).toLocaleDateString('fr', { year: 'numeric', month: 'short', day: 'numeric' }) }}</time>
        <span class="text-gray-500 dark:text-gray-400">&middot;</span>
        <span class="text-gray-500 dark:text-gray-400">{{post.meta.readingTime > 1 ? `${post.meta.readingTime} mins de lecture` : '1 min de lecture' }}</span>
      </div>
      <div class="flex flex-wrap items-center gap-3 mt-4">
        <UButton
            v-for="(author, index) in post.authors"
            :key="index"
            color="neutral"
            variant="outline"
            size="sm"
        >
          <UAvatar
              v-bind="author.avatar"
              :alt="author.name"
              size="xs"
          />

          {{ author.name }}
        </UButton>
      </div>
    </UPageHeader>

    <UPage>
      <UPageBody prose>
        <ContentRenderer
            v-if="post && post.body"
            :value="post"
        />

        <USeparator v-if="surround?.filter(Boolean).length" />

        <UContentSurround :surround="(surround as any)">
          <template #link-title="{link}">
            <MDC :value="link.title" unwrap="p" />
          </template>
        </UContentSurround>
      </UPageBody>

      <template #right>
        <UContentToc :ui="{linkText: 'text-ellipsis'}"
            v-if="post.body && post.body.toc && post.body.toc.links.length"
            :links="post.body.toc.links"
            title="Sommaire"
        />
      </template>
    </UPage>
  </UContainer>
</template>